// Track color resolution.
//
// Backend/mobile stamps default colors onto tracks (e.g. "#ebc", "589500") that the
// web can't represent and that users didn't choose. The web only honors a stored
// color if it matches one of the user-selectable palette swatches; anything else
// (missing, unparseable, or a system default) falls back to red.

// Matches the 14 marker colors (marker-details/dummy.js ColorCodes), rendered as
// two rows of 7 in the track editor.
export const TRACK_COLORS = [
  '#589500', '#9ECC3B', '#FF0000', '#FF6800', '#FFD533', '#00E5FF', '#3399FF',
  '#984AF1', '#F7F7F7', '#808080', '#000000', '#996633', '#00FFCC', '#F704FF'
]
export const DEFAULT_TRACK_COLOR = '#FF0000'
// Free tier: only this color is selectable; the rest are dimmed/locked.
export const FREE_COLOR = '#FF0000'

// Default line style for free-drawn routes (used by the Route Builder). WEB-7046.
export const FREE_LINE_STYLE = 'dots'

// Normalize a stored color to "#RRGGBB" uppercase, or '' if not a full 6-digit hex.
const normalizeHex = (c) => {
  if (!c) return ''
  let s = String(c).trim().toUpperCase()
  if (s[0] === '#') s = s.slice(1)
  if (!/^[0-9A-F]{6}$/.test(s)) return '' // reject 3-digit (#ebc), alpha, or junk
  return `#${s}`
}

/**
 * Resolve the color to display/edit for a track from its geoJson `line` object.
 *
 * The `line.style` tag ('dots'|'dashed'|'solid') is written only by the web editor;
 * mobile/legacy tracks omit it. So a missing `style` means the track has never been
 * edited in the web — ignore its stamped default color and use red. Once it has been
 * web-edited, honor the chosen color.
 */
export const resolveTrackColor = (line) => {
  const l = line || {}
  if (!l.style) return DEFAULT_TRACK_COLOR
  return normalizeHex(l.color) || DEFAULT_TRACK_COLOR
}

// Selectable track line widths (value = px stroke weight; label shown in the editor).
export const TRACK_WIDTHS = [
  { value: 4, label: 'Regular' },
  { value: 6, label: 'Bold' },
  { value: 8, label: 'Heavy' }
]
export const DEFAULT_TRACK_WIDTH = 4

// Clamp a stored width to a positive number, defaulting to Regular (4).
export const normalizeTrackWidth = (w) => {
  const n = Number(w)
  return n > 0 ? n : DEFAULT_TRACK_WIDTH
}

/**
 * Google Maps polyline options for a track's color + line style + width.
 * Shared by the map renderer and the edit preview so styling stays consistent.
 * Widths scale relative to the Regular (4px) baseline: dots/dashes grow with width,
 * solid uses the width directly as the stroke weight.
 */
export const trackPolylineOptions = (color, style, width) => {
  const g = typeof google !== 'undefined' ? google : null
  const w = normalizeTrackWidth(width)
  const k = w / DEFAULT_TRACK_WIDTH // 1 at Regular(4), 1.5 at Bold(6), 2 at Heavy(8)
  if (style === 'dots' && g) {
    return {
      strokeColor: color, strokeOpacity: 0, strokeWeight: w,
      icons: [{ icon: { path: g.maps.SymbolPath.CIRCLE, scale: 2.2 * k, fillColor: color, fillOpacity: 1, strokeColor: color, strokeOpacity: 1 }, offset: '0', repeat: `${12 * k}px` }]
    }
  }
  if (style === 'dashed' && g) {
    return {
      strokeColor: color, strokeOpacity: 0, strokeWeight: w,
      icons: [{ icon: { path: 'M 0,-1 0,1', strokeColor: color, strokeOpacity: 1, scale: 3 * k }, offset: '0', repeat: `${18 * k}px` }]
    }
  }
  return { strokeColor: color, strokeOpacity: 1, strokeWeight: w, icons: [] }
}
